/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { mainNavigationStyles } from "@insite/content-library/Widgets/Header/MainNavigation";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import { navigationDrawerStyles } from "@monsma/Overrides/Components/NavigationDrawer";
import { screen } from "@monsma/XDL/Helpers";
import UserCircle from "@monsma/XDL/Icons/UserCircle";
import { mergeWithCss } from "@monsma/XDL/Utilities";
import { css } from "styled-components";

mainNavigationStyles.mobileMenuWrapper = {
    css: css`
        padding: 15px 7px;

        ${screen("lte", "sm")} {
            margin-left: 10px;
        }
    `,
};

navigationDrawerStyles.menuTriggerButton = {
    css: css`
        padding: 5px;
        ${IconWrapper} {
            color: ${getColor("common.ui9")};
        }
        ${({ drawerIsOpen }: any) =>
            drawerIsOpen &&
            css`
                ${IconWrapper} {
                    color: ${getColor("common.ui4")};
                }
            `}
    `,
};

navigationDrawerStyles!.drawer!.cssOverrides!.drawerContent = css`
    overflow-x: hidden;
`;
navigationDrawerStyles!.drawer!.cssOverrides!.scrim = css`
    ${({ notificationBarState }: any) => (notificationBarState ? `top: 104px;` : `top: 64px;`)}
`;

navigationDrawerStyles!.drawer!.cssOverrides!.drawerTitle = css`
    display: none;
`;

navigationDrawerStyles!.drawer!.cssOverrides!.drawerBody = css`
    background: ${getColor("common.background")};
`;

navigationDrawerStyles.drawerSectionWrapper = {
    css: css`
        margin-top: 10px;
        margin-bottom: 10px;
        border-bottom: 1px solid ${getColor("common.border")};
        padding-bottom: 10px;

        &:last-child {
            margin-bottom: 0;
            border: 0;
        }
    `,
};

navigationDrawerStyles.panelMenu = {
    ...navigationDrawerStyles.panelMenu,
    headerColor: "common.background",
    bodyColor: "common.background",
    panelRowProps: {
        color: "common.background",
        isCurrentCss: css`
            &&& {
                background: ${getColor("common.ui3")};

                span {
                    color: white;
                }
            }
        `,
        moreIconProps: {
            src: "ChevronRight",
            size: 20,
            color: "common.ui9",
            css: css`
                color: ${getColor("common.ui9")};
            `,
        },
    },
    closeIconProps: {
        css: css`
            display: none;
        `,
    },
    backIconProps: {
        size: 20,
        css: css`
            &&& {
                left: -20px;
                color: ${getColor("common.ui9")};

                &:after {
                    content: "Back";
                    font-weight: bold;
                    position: absolute;
                    left: 25px;
                    line-height: 20px;
                    font-size: 14px;
                    color: ${getColor("common.ui10")};
                }
            }
        `,
    },
    cssOverrides: {
        children: css`
            &&& {
                > div {
                    &:first-child {
                        padding: 0 0 10px;
                        margin-top: 10px;
                        border-bottom: 1px solid ${getColor("common.border")};

                        button {
                            height: 44px;
                        }
                    }

                    &:last-child {
                        margin-top: 10px;
                    }
                }
            }
        `,
        wrapper: css`
            > button {
                padding: 12px 17px;
            }
        `,
    },
};

navigationDrawerStyles.userRowTypography = {
    color: "common.ui10",
    weight: "bold",
    ellipsis: true,
    transform: "capitalize",
};

navigationDrawerStyles.punchOutUserRowText = {
    color: "common.ui10",
    transform: "capitalize",
};

navigationDrawerStyles.mainNavigationRow = {
    color: "common.background",
    isCurrentCss: navigationDrawerStyles?.panelMenu?.panelRowProps?.isCurrentCss,
    moreIconProps: {
        src: "ChevronRight",
        size: 20,
        color: "common.ui9",
        css: css`
            color: ${getColor("common.ui9")};
        `,
    },
    css: css`
        padding: 12px 17px;
        line-height: 20px;
    `,
};

navigationDrawerStyles.mainNavigationRowIcon = {
    src: UserCircle,
    color: "common.ui9",
    size: 15,
    css: css`
        margin-right: 10px;
        display: inline-block;
        text-align: center;
        vertical-align: middle;
    `,
};

navigationDrawerStyles.mainNavigationRowTypography = {
    ...navigationDrawerStyles.mainNavigationRowTypography,
    color: "common.ui10",
    transform: "capitalize",
    size: "14px",
    lineHeight: "20px",
};

navigationDrawerStyles.logoLinks = {
    typographyProps: {
        ...navigationDrawerStyles?.logoLinks?.typographyProps,
        weight: "bold",
        transform: "capitalize",
        size: "14px",
        lineHeight: "20px",
    },
    color: "common.background",
    moreIconProps: {
        src: "ChevronRight",
        size: 20,
        color: "common.ui9",
        css: css`
            color: ${getColor("common.ui9")};
        `,
    },
};

navigationDrawerStyles.signOutRow = {
    css: css`
        background: ${getColor("common.background")};
    `,
};

navigationDrawerStyles.signOutRowText = {
    weight: "bold",
    transform: "capitalize",
    size: "14px",
};

navigationDrawerStyles.xStyle = {
    link: {
        color: "common.ui10",
        css: css`
            margin-right: 35px;
        `,
        typographyProps: {
            css: css`
                font-size: 14px;
                line-height: 20px;
                font-weight: bold;
                text-transform: capitalize;
            `,
        },
    },
};

navigationDrawerStyles.menuRowIcon = {
    size: 16,
    color: "common.ui9",
    css: css`
        margin-right: 10px;
        display: inline-block;
        text-align: center;
        vertical-align: middle;
    `,
};

navigationDrawerStyles.panelSectionWrapper = {
    css: css`
        display: flex;
        align-items: center;
    `,
};

navigationDrawerStyles.changeCustomerRow = {
    css: css`
        padding: 15px 15px 15px 20px;
    `,
};

navigationDrawerStyles.drawer = mergeWithCss(navigationDrawerStyles.drawer, {
    css: css``,
});
